// Lightweight fetch wrapper that:
//  - Always sends credentials: 'include' (cookie fallback)
//  - Injects Authorization: Bearer <token> from localStorage if present (survives
//    edge cases where cookies get stripped by CDNs / SameSite / privacy modes)
//  - Surfaces network + API failures as toast notifications
//  - Opt-out via { silent: true } for expected 401 probes
import { toast } from 'sonner';

const TOKEN_KEY = 'nwth_session_token';

export function setStoredToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch (err) {
    // localStorage might be disabled (private browsing / iframe sandbox);
    // cookie fallback still works — log for observability.
    console.debug('setStoredToken: localStorage unavailable', err);
  }
}

export function getStoredToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function clearStoredToken() {
  setStoredToken(null);
}

export async function apiFetch(url, options = {}) {
  const { silent, ...init } = options;
  const headers = new Headers(init.headers || {});
  const token = getStoredToken();
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  const finalInit = { credentials: 'include', ...init, headers };

  let res;
  try {
    res = await fetch(url, finalInit);
  } catch (err) {
    if (!silent) {
      toast.error('Network error', {
        description: 'Could not reach the server. Check your connection and try again.',
      });
    }
    throw err;
  }

  if (!res.ok && !silent) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = await res.clone().json();
      if (body?.detail) detail = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail);
    } catch (err) {
      // Response body isn't JSON (e.g., HTML error page) — status text is used.
      console.debug('apiFetch: non-JSON error body', err);
    }
    // Don't toast for 401 on auth probes (handled by callers redirecting to /login)
    if (res.status !== 401) {
      toast.error('Request failed', { description: detail });
    }
  }

  return res;
}

export { toast };
